import React from 'react';
import { formatCurrency } from '@/utils/money';
import { PricingRecommendationRow } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { PricingTableDeltaCell } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { resolveDelta } from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';

interface Props {
  currencyCode: string;
  row: PricingRecommendationRow;
}

export const AgentWeeklyGPImpactCell = React.memo<Props>(
  ({ currencyCode, row }) => {
    const overridePriceInMicros = useRecommendationActionsStore(
      (state) => state.actions[row.uuid]?.overridePriceInMicros,
    );

    // Preserve the server-computed impact when there's no override. With an
    // override, shift the impact by the per-unit price delta * weekly volume
    // so the displayed impact tracks the user's edit without needing to
    // reverse-engineer the server's full formula.
    const baseImpactInMicros = row.weeklyGPImpactInMicros ?? 0;
    const weeklyVolume = row.weeklyVolume ?? 0;
    const priceDeltaInMicros =
      overridePriceInMicros !== undefined
        ? overridePriceInMicros - row.newPriceInMicros
        : 0;
    const adjustedImpactInMicros =
      baseImpactInMicros + priceDeltaInMicros * weeklyVolume;

    const { direction, formattedValue } = resolveDelta(
      adjustedImpactInMicros,
      (v) => formatCurrency(v, currencyCode),
    );

    return (
      <PricingTableDeltaCell
        arrowDirection={direction}
        value={formattedValue}
      />
    );
  },
);
